import { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { BUTTON_LABEL, LABELS, SEARCH_DATA } from "../../../../../shared/constants";
import { dateAndTimeConvert } from "../../../../../shared/helperFunc/dateConvert";
import authProvider from "../../../../../shared/config/authProvider";
import CustomModal from "../../../../../shared/components/CustomModal";
import Table from "../../../../../shared/components/Table";
import SimpleButton from "../../../../../shared/components/Button/Button";
import {
  useAuditTrailListQuery,
  AuditTrailEntry,
} from "../../../../../Features/audit/audit-slice";

interface AuditTrailProps {
  orderId: string;
}

const VALUE_TRUNCATE_LENGTH = 100;

const formatDisplayValue = (value: string | null, fieldName?: string): string => {
  if (
    value === null ||
    value === "" ||
    value === "[]" ||
    value === "{}" ||
    value?.trim() === "[]" ||
    value?.trim() === "{}" ||
    value?.trim() === "null"
  ) {
    return LABELS.EM_DASH_TEXT;
  }

  if (
    fieldName &&
    (fieldName.endsWith("At") || fieldName.endsWith("Date") || fieldName === "paymentAt")
  ) {
    if (/^\d{10,13}$/.test(value) || (!isNaN(Date.parse(value)) && isNaN(Number(value)))) {
      const formatted = dateAndTimeConvert(value);
      if (formatted && formatted !== "Invalid Date") {
        return formatted;
      }
    }
  }

  // Parse and format measurement JSON strings into human readable text
  if (value.startsWith("[") || (fieldName && fieldName.startsWith("measurement"))) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return LABELS.EM_DASH_TEXT;
        const parts = parsed.map((item: any) => {
          const typeStr = item.type ? `${item.type}: ` : "";
          const dataStr = Array.isArray(item.data)
            ? item.data
                .filter((d: any) => d.title && d.value)
                .map((d: any) => `${d.title}: ${d.value}`)
                .join(", ")
            : "";
          const remarksStr = item.remarks ? ` (${item.remarks})` : "";
          return `${typeStr}${dataStr}${remarksStr}`;
        });
        const formatted = parts.filter(Boolean).join(" | ");
        if (formatted) return formatted;
        return LABELS.EM_DASH_TEXT;
      }
    } catch (e) {
      // Fall through if not valid JSON
    }
  }

  return value;
};

// Per-cell expander for long values (US-OO-03). "Show More" reveals the full
// value; "Show Less" collapses it back to the truncated prefix.
const ValueCell = ({
  value,
  fieldName,
}: {
  value: string | null;
  fieldName?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  const formatted = formatDisplayValue(value, fieldName);

  if (formatted === null || formatted === "" || formatted === LABELS.EM_DASH_TEXT) {
    return <>{LABELS.EM_DASH_TEXT}</>;
  }

  const isLong = formatted.length > VALUE_TRUNCATE_LENGTH;
  if (!isLong) {
    return <>{formatted}</>;
  }

  const shown = expanded
    ? formatted
    : `${formatted.substring(0, VALUE_TRUNCATE_LENGTH)}…`;

  return (
    <>
      <span>{shown}</span>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? LABELS.SHOW_LESS_TEXT : LABELS.SHOW_MORE_TEXT}
      </button>
    </>
  );
};


const initialSort = {
  ...SEARCH_DATA,
  sortBy: "updatedAt",
};

const AuditTrail = ({ orderId }: AuditTrailProps) => {
  const [showModal, setShowModal] = useState(false);
  const [sort, setSort] = useState(initialSort);
  const [count, setCount] = useState(0);
  const [auditList, setAuditList] = useState<AuditTrailEntry[]>([]);

  const handleOpen = () => {
    setSort(initialSort);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSort(initialSort);
  };

  useEffect(() => {
    if (!showModal) {
      setSort(initialSort);
    }
  }, [showModal]);

  const queryArgs = useMemo(
    () => ({
      orderId,
      limit: sort.limit,
      skip: sort.skip,
      sort: sort.sort,
      sortBy: sort.sortBy,
    }),
    [orderId, sort]
  );

  // Fetch only once the modal opens — never for users who don't click.
  const { data, isSuccess, isFetching } = useAuditTrailListQuery(
    showModal ? queryArgs : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (isSuccess && data) {
      setAuditList(data.list);
      setCount(data.count);
    }
  }, [data, isSuccess]);

  const columns = useMemo(
    () => [
      {
        header: LABELS.ITEM_CODE_TEXT || "Item Code",
        cell: (row: any) => {
          const itemCode = row?.row?.original?.itemCode;
          return itemCode ? (
            <span className="fw-bold">{itemCode}</span>
          ) : (
            <span className="text-muted">{LABELS.EM_DASH_TEXT}</span>
          );
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.STATUS_TEXT || "Status",
        cell: (row: any) => {
          const action = (row?.row?.original?.action || "UPDATE").toUpperCase();
          let bgColor = "#FFF3CD";
          let textColor = "#856404";
          let borderColor = "#FFEEBA";
          let label = "Updated";

          if (action === "CREATE") {
            bgColor = "#D4EDDA";
            textColor = "#155724";
            borderColor = "#C3E6CB";
            label = "Created";
          } else if (action === "DELETE") {
            bgColor = "#F8D7DA";
            textColor = "#721C24";
            borderColor = "#F5C6CB";
            label = "Deleted";
          }

          return (
            <span
              style={{
                backgroundColor: bgColor,
                color: textColor,
                border: `1px solid ${borderColor}`,
                padding: "3px 10px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                display: "inline-block",
                textAlign: "center",
                minWidth: "70px",
              }}
            >
              {label}
            </span>
          );
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.FIELD_NAME_TEXT,
        cell: (row: any) => {
          const field = row?.row?.original?.fieldName;
          const customLabels: Record<string, string> = {
            itemCode: "Item Code",
            qty: "Quantity",
            price: "Price",
            wsp: "WSP",
            size: "Size",
            color: "Color",
            remarks: "Remarks",
            designNo: "Design No",
            fabricCode: "Fabric Code",
            salesPerson: "Sales Person",
            firstName: "Customer Name",
            customerName: "Customer Name",
            customerMobile: "Phone",
            address: "Address",
            shortCode: "Short Code",
            store: "Store",
            user: "User",
            checkerId: "Checker Id",
            deliveryDate: "Store Delivery Date",
            custDeliveryDate: "Customer Delivery Date",
            advanceAmount: "Advance Amount",
            advanceRemarks: "Advance Remark",
            "advancePay.amount": "Advance Amount",
            "advancePay.remarks": "Advance Remark",
            "measurement.top.sherwani": "Top Measurement - Sherwani",
            "measurement.top.indo": "Top Measurement - Indo",
            "measurement.top.jodhpuri": "Top Measurement - Jodhpuri",
            "measurement.top.coat": "Top Measurement - Coat",
            "measurement.top.alika": "Top Measurement - Alika",
            "measurement.top.kurta": "Top Measurement - Kurta",
            "measurement.top.bandi": "Top Measurement - Bandi",
            "measurement.bottom": "Bottom Measurement",
            "measurement.custom.styleAndFabric": "Customization - Style & Fabric",
            "measurement.custom.cutStyle": "Customization - Cut Style",
            "measurement.custom.fabricChange": "Customization - Fabric Change",
            "measurement.custom.workChange": "Customization - Work Change",
            "measurement.custom.bodyInstruction": "Customization - Body Instruction",
            "measurement.accessories.mojdiShoes": "Accessories - Mojdi Shoes",
            "measurement.accessories.dupatta": "Accessories - Dupatta",
            "measurement.accessories.kamarbandh": "Accessories - Kamarbandh",
          };
          return customLabels[field] || field || LABELS.EM_DASH_TEXT;
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.PREVIOUS_VALUE_TEXT,
        cell: (row: any) => (
          <ValueCell
            value={row?.row?.original?.previousValue}
            fieldName={row?.row?.original?.fieldName}
          />
        ),
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.NEW_VALUE_TEXT,
        cell: (row: any) => (
          <ValueCell
            value={row?.row?.original?.newValue}
            fieldName={row?.row?.original?.fieldName}
          />
        ),
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.CHANGED_BY_TEXT,
        cell: (row: any) => {
          const user = row?.row?.original?.updatedBy;
          if (!user) return LABELS.EM_DASH_TEXT;
          return user.name || user.username || user._id || LABELS.EM_DASH_TEXT;
        },
        isShow: true,
        enableSorting: false,
      },
      {
        header: LABELS.TIMESTAMP_TEXT,
        cell: (row: any) => {
          const changedAt = row?.row?.original?.updatedAt;
          return changedAt ? dateAndTimeConvert(changedAt) : LABELS.EM_DASH_TEXT;
        },
        isShow: true,
        enableSorting: false,
      },
    ],
    []
  );

  // Read-only: no edit/delete controls, no mutation hooks, no row-click handler.
  const canView = authProvider.show("order", "auditTrail");
  if (!canView) {
    return null;
  }

  return (
    <>
      <div className="mb-3">
        <SimpleButton
          className="btn theme-btn theme-btn-sm me-2"
          onClick={handleOpen}
          text={LABELS.VIEW_AUDIT_TRAIL_TEXT}
        />
      </div>

      <CustomModal
        show={showModal}
        close={handleClose}
        size="xl"
        message=""
        modalTitle={LABELS.AUDIT_TRAIL_TEXT}
        modalButton={[]}
        fullscreen={undefined}
        className=""
        centerClass=""
        modalTitleIcon=""
        footerClass=""
      >
        <Table
          data={auditList}
          columns={columns}
          sort={sort}
          setSort={setSort}
          coloumnDrop={false}
          count={count || undefined}
          isFetching={isFetching}
        />
      </CustomModal>
    </>
  );
};

export default AuditTrail;
